import React, { createContext, useContext, useState, useEffect } from 'react'
import axios, { AxiosError } from 'axios'
import { useClerk } from '@clerk/clerk-react'

interface User {
  id: string
  email: string
  role: string
}

interface AuthContextType {
  user: User | null
  loginWithClerk: (clerkToken: string) => Promise<void>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Add debug logging utility
const debugLog = (stage: string, data?: any) => {
  const timestamp = new Date().toISOString()
}

// Add error logging utility
const errorLog = (stage: string, error: any) => {
  const timestamp = new Date().toISOString()
  console.error(`❌ [AUTH ERROR ${timestamp}] ${stage}`, error)
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const clerk = useClerk()

  useEffect(() => {
    debugLog('useEffect hook triggered - checking for existing token')
    
    const initializeAuth = async () => {
      try {
        debugLog('Starting authentication initialization')
        
        const token = localStorage.getItem('admin-token')
        debugLog('Token check result', { 
          hasToken: !!token, 
          tokenLength: token?.length || 0,
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'no token'
        })

        if (!token) {
          debugLog('No token found - setting loading to false')
          setLoading(false)
          return
        }

        debugLog('Token found - attempting API call to validate')
        
        // Add timeout and detailed error handling
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
          debugLog('API call timeout after 10 seconds - aborting request')
          controller.abort()
        }, 10000) // 10 second timeout

        try {
          debugLog('Making API call to /api/admin/me', {
            url: '/api/admin/me',
            method: 'GET',
            headers: { Authorization: `Bearer ${token.substring(0, 20)}...` }
          })

          const response = await axios.get('/api/admin/me', {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            timeout: 8000 // 8 second axios timeout
          })

          clearTimeout(timeoutId)
          debugLog('API call successful', { 
            status: response.status, 
            userData: response.data 
          })

          setUser(response.data.user)
          debugLog('User state updated successfully')
          
        } catch (apiError) {
          clearTimeout(timeoutId)
          
          if (axios.isCancel(apiError)) {
            errorLog('API call was cancelled/aborted', { reason: 'timeout or manual abort' })
          } else if (apiError instanceof AxiosError) {
            errorLog('Axios error during API call', {
              message: apiError.message,
              code: apiError.code,
              status: apiError.response?.status,
              statusText: apiError.response?.statusText,
              url: apiError.config?.url,
              timeout: apiError.config?.timeout,
              isTimeout: apiError.code === 'ECONNABORTED'
            })
            
            // Only remove token on 401/403 (unauthorized), not on network errors
            if (apiError.response?.status === 401 || apiError.response?.status === 403) {
              debugLog('Removing invalid token from localStorage - authentication failed')
              localStorage.removeItem('admin-token')
              setUser(null)
            } else {
              errorLog('Network/API error - keeping token for retry', {
                status: apiError.response?.status,
                keepingToken: true
              })
              setUser(null) // Clear user state but keep token for retry
            }
          } else {
            errorLog('Unknown error during API call', apiError)
            setUser(null) // Don't remove token on unknown errors
          }
        }

      } catch (error) {
        errorLog('Error in authentication initialization', error)
        setUser(null)
      } finally {
        debugLog('Setting loading to false - authentication initialization complete')
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const loginWithClerk = async (clerkToken: string) => {
    debugLog('Clerk login exchange started')

    try {
      setLoading(true)

      const response = await axios.post('/api/admin/clerk-login', {}, {
        headers: { Authorization: `Bearer ${clerkToken}` },
        timeout: 10000
      })

      const { token, user: userData } = response.data
      localStorage.setItem('admin-token', token)
      setUser(userData)
      setLoading(false)

      debugLog('Clerk login exchange completed successfully', { userId: userData.id })

    } catch (error) {
      setLoading(false)
      errorLog('Clerk login exchange failed', error)
      throw error
    }
  }

  const logout = () => {
    debugLog('Logout initiated')
    localStorage.removeItem('admin-token')
    setUser(null)
    clerk.signOut().catch((err) => errorLog('Clerk sign-out failed', err))
    debugLog('Logout completed')
  }

  const contextValue = {
    user,
    loginWithClerk,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 